import { useState } from 'react';
import {
  IconCheck, IconX, IconUser, IconUserPlus, IconShield, IconPencil,
  IconTrash, IconStar, IconGift, IconCoffee,
} from '@tabler/icons-react';
import { Modal } from '../components/ui';

/**
 * /design-guide — visual design system reference.
 *
 * Single page where every reusable pattern from the DreamsPOS template
 * (extracted into `template-design-patterns.md` in memory) is rendered
 * live, with the exact class string used. The user can scroll through
 * to review/approve/correct patterns before they're used in real
 * modules. Public route — no auth — so it's easy to share.
 *
 * Add new patterns here AT THE SAME TIME as the catalogue file, so
 * the pattern is visible (this page) and reference-able (markdown).
 */

// ---- Layout primitives ----------------------------------------------------

function Section({
  number, title, source, children,
}: { number: string; title: string; source: string; children: React.ReactNode }) {
  return (
    <section className="mb-12 scroll-mt-6" id={`section-${number}`}>
      <header className="mb-5 pb-3 border-b-2 border-gray-200">
        <div className="flex items-baseline gap-3 flex-wrap">
          <span className="text-13 font-bold text-primary tabular-nums">{number}</span>
          <h2 className="text-22 font-bold text-heading">{title}</h2>
        </div>
        <p className="text-13 text-gray-600 mt-1">
          Source: <code className="text-12 bg-gray-100 px-1.5 py-0.5 rounded">{source}</code>
        </p>
      </header>
      <div className="space-y-6">{children}</div>
    </section>
  );
}

function Pattern({
  label, note, classes, children, span = false,
}: {
  label: string;
  note?: string;
  classes?: string;
  children: React.ReactNode;
  span?: boolean;
}) {
  return (
    <div className={`grid ${span ? 'grid-cols-1' : 'md:grid-cols-[1fr_320px]'} gap-4 items-start`}>
      <div className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm">
        <p className="text-12 font-bold uppercase tracking-wider text-gray-500 mb-3">{label}</p>
        {children}
      </div>
      {!span && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          {note && <p className="text-13 text-heading font-semibold mb-2">{note}</p>}
          {classes && (
            <code className="block text-12 leading-relaxed text-gray-700 font-mono whitespace-pre-wrap break-words">
              {classes}
            </code>
          )}
        </div>
      )}
    </div>
  );
}

function DoDont({ doEl, dontEl, doNote, dontNote }: {
  doEl: React.ReactNode; dontEl: React.ReactNode; doNote: string; dontNote: string;
}) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="bg-white border-2 border-success rounded-lg p-5">
        <div className="flex items-center gap-2 mb-3">
          <span className="inline-flex items-center justify-center w-6 h-6 rounded bg-success text-[#FFFFFF]">
            <IconCheck size={14} stroke={3} />
          </span>
          <span className="text-13 font-bold uppercase tracking-wider text-success">Do</span>
        </div>
        <div className="mb-3">{doEl}</div>
        <p className="text-13 text-gray-700">{doNote}</p>
      </div>
      <div className="bg-white border-2 border-danger rounded-lg p-5">
        <div className="flex items-center gap-2 mb-3">
          <span className="inline-flex items-center justify-center w-6 h-6 rounded bg-danger text-[#FFFFFF]">
            <IconX size={14} stroke={3} />
          </span>
          <span className="text-13 font-bold uppercase tracking-wider text-danger">Don't</span>
        </div>
        <div className="mb-3">{dontEl}</div>
        <p className="text-13 text-gray-700">{dontNote}</p>
      </div>
    </div>
  );
}

// ---- Page ---------------------------------------------------------------

export default function DesignGuidePage() {
  const [modalOpen, setModalOpen] = useState(false);

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Top bar */}
      <header className="sticky top-0 z-10 bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-18 font-bold text-heading">CODED. POS — Design Guide</h1>
            <p className="text-12 text-gray-600 mt-0.5">
              Patterns extracted from DreamsPOS template · single source of truth for new modules
            </p>
          </div>
          <a
            href="/pos"
            className="text-13 font-semibold text-primary hover:underline"
          >
            ← Back to POS
          </a>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 py-8">
        {/* Intro */}
        <div className="mb-10 p-5 rounded-lg bg-primary-soft border-2 border-primary/30">
          <h2 className="text-16 font-bold text-heading mb-2">How to use this guide</h2>
          <p className="text-14 text-heading leading-relaxed">
            Every pattern below is the canonical rendering for that component type
            in the POS. When building a new screen, find the closest pattern here
            and match it — don't improvise. If you spot anything that should be
            different, flag it and we'll update both this guide and the
            <code className="text-12 bg-white px-1.5 py-0.5 rounded mx-1">template-design-patterns.md</code>
            spec.
          </p>
        </div>

        {/* Quick nav */}
        <nav className="mb-12 p-4 rounded-lg bg-white border border-gray-200">
          <p className="text-12 font-bold uppercase tracking-wider text-gray-500 mb-2.5">Jump to</p>
          <ul className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1.5 text-13 font-semibold">
            {[
              ['1', 'Colour'], ['2', 'Typography'], ['3', 'Buttons'],
              ['4', 'Badges'], ['5', 'Form fields'], ['6', 'Cards'],
              ['7', 'List rows'], ['8', 'Modals'], ['9', 'Welcome / Info'],
              ['10', 'Common mistakes'],
            ].map(([n, t]) => (
              <li key={n}>
                <a className="text-primary hover:underline" href={`#section-${n}`}>{n}. {t}</a>
              </li>
            ))}
          </ul>
        </nav>

        {/* 1. COLOUR */}
        <Section number="1" title="Colour palette" source="tailwind.config.ts">
          <Pattern label="Brand + semantic tokens" span
            note="Each colour ships with DEFAULT, hover, and a soft transparent tint.">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { name: 'primary', cls: 'bg-primary' },
                { name: 'secondary', cls: 'bg-secondary' },
                { name: 'success', cls: 'bg-success' },
                { name: 'danger', cls: 'bg-danger' },
                { name: 'warning', cls: 'bg-warning' },
                { name: 'info', cls: 'bg-info' },
                { name: 'orange', cls: 'bg-orange' },
                { name: 'purple', cls: 'bg-purple' },
                { name: 'pink', cls: 'bg-pink' },
                { name: 'teal', cls: 'bg-teal' },
                { name: 'indigo', cls: 'bg-indigo' },
                { name: 'dark', cls: 'bg-dark' },
              ].map(c => (
                <div key={c.name}>
                  <div className={`h-12 rounded-md ${c.cls} shadow-sm`} />
                  <p className="text-12 font-mono text-gray-700 mt-1.5">{c.name}</p>
                </div>
              ))}
            </div>
          </Pattern>

          <Pattern label="Soft tint pairs (badge bg / text)" span
            note="Soft variants always pair pale bg with the FULL-saturation text — both colours come from the same hue family.">
            <div className="flex flex-wrap gap-2">
              <span className="inline-flex items-center text-13 font-medium px-2.5 py-1 rounded bg-success-soft text-success">success-soft</span>
              <span className="inline-flex items-center text-13 font-medium px-2.5 py-1 rounded bg-danger-soft text-danger">danger-soft</span>
              <span className="inline-flex items-center text-13 font-medium px-2.5 py-1 rounded bg-warning-soft text-warning">warning-soft</span>
              <span className="inline-flex items-center text-13 font-medium px-2.5 py-1 rounded bg-info-soft text-info">info-soft</span>
              <span className="inline-flex items-center text-13 font-medium px-2.5 py-1 rounded bg-purple-soft text-purple">purple-soft</span>
              <span className="inline-flex items-center text-13 font-medium px-2.5 py-1 rounded bg-orange-soft text-orange">orange-soft</span>
            </div>
          </Pattern>
        </Section>

        {/* 2. TYPOGRAPHY */}
        <Section number="2" title="Typography" source="tailwind.config.ts (sizes 10-22) + Instrument Sans 400-700">
          <Pattern
            label="Sizes"
            note="Floor for any sub-text is text-13. Never text-11 for content the user has to read."
            classes={`text-22 font-bold     // page title
text-18 font-bold     // section heading
text-16 font-bold     // card title (h4 equivalent)
text-15 font-semibold // emphasised body
text-14 font-medium   // default body / label / button
text-13 font-medium   // sub-row, helper
text-12 font-bold uppercase tracking-wider // eyebrow`}
          >
            <p className="text-22 font-bold text-heading">Page title — 22 / bold</p>
            <p className="text-18 font-bold text-heading mt-2">Section heading — 18 / bold</p>
            <p className="text-16 font-bold text-heading mt-2">Card title — 16 / bold</p>
            <p className="text-15 font-semibold text-heading mt-2">Emphasised body — 15 / semibold</p>
            <p className="text-14 font-medium text-heading mt-2">Default body — 14 / medium</p>
            <p className="text-13 font-medium text-gray-700 mt-2">Sub-row — 13 / medium / gray-700</p>
            <p className="text-12 font-bold uppercase tracking-wider text-gray-500 mt-2">Eyebrow — 12 / bold / uppercase</p>
          </Pattern>

          <Pattern
            label="Weights available"
            note="Instrument Sans loads 400-700 only. font-extrabold/black map to 700 in the config — use SIZE + LETTER-SPACING + COLOUR for visually heavier emphasis."
            classes={`font-regular  (400)
font-medium   (500)
font-semibold (600)
font-bold     (700) ← max real weight`}
          >
            <p className="text-15 font-regular text-heading">Regular 400</p>
            <p className="text-15 font-medium text-heading">Medium 500</p>
            <p className="text-15 font-semibold text-heading">Semibold 600</p>
            <p className="text-15 font-bold text-heading">Bold 700</p>
          </Pattern>

          <Pattern
            label="Text colour floor"
            note="Anything important to read = text-heading or text-gray-700. text-gray-500 only for true micro-print."
            classes={`text-heading   // primary readable
text-gray-700  // sub-row, secondary
text-gray-600  // helper
text-gray-500  // eyebrows, captions
text-gray-400  // ❌ never for content`}
          >
            <p className="text-14 text-heading">text-heading — name, title, value</p>
            <p className="text-14 text-gray-700 mt-1">text-gray-700 — sub-row, meta</p>
            <p className="text-14 text-gray-600 mt-1">text-gray-600 — helper, hint</p>
            <p className="text-14 text-gray-500 mt-1">text-gray-500 — eyebrow, caption</p>
            <p className="text-14 text-gray-400 mt-1 line-through">text-gray-400 — never for content</p>
          </Pattern>
        </Section>

        {/* 3. BUTTONS */}
        <Section number="3" title="Buttons" source="pos.html, customer.html · style.css:3672+">
          <Pattern
            label="Primary CTA — solid filled"
            note="38px default (md), 44px touch (lg), 48px hero (xl). font-semibold."
            classes={`h-12 px-5 rounded-md
bg-primary text-[#FFFFFF] font-bold
hover:bg-primary-hover
shadow-sm`}
          >
            <button className="h-12 px-5 inline-flex items-center gap-2 rounded-md bg-primary text-[#FFFFFF] text-14 font-bold hover:bg-primary-hover shadow-sm">
              <IconUserPlus size={18} stroke={2.5} /> Primary CTA
            </button>
          </Pattern>

          <Pattern
            label="Secondary / cancel — light filled rectangle"
            note="Same shape and height as primary. Never a thin text link — that's the 'looks like an afterthought' anti-pattern."
            classes={`h-12 px-5 rounded-md
bg-light text-heading font-bold
border border-border
hover:bg-light-hover`}
          >
            <button className="h-12 px-5 inline-flex items-center justify-center rounded-md bg-light text-heading text-14 font-bold border border-border hover:bg-light-hover transition-colors">
              Cancel
            </button>
          </Pattern>

          <Pattern
            label="Danger — destructive"
            classes={`h-12 px-5 rounded-md
bg-danger text-[#FFFFFF] font-bold
hover:bg-danger-hover
shadow-sm`}
          >
            <button className="h-12 px-5 inline-flex items-center gap-2 rounded-md bg-danger text-[#FFFFFF] text-14 font-bold hover:bg-danger-hover shadow-sm">
              <IconTrash size={16} stroke={2.5} /> Delete
            </button>
          </Pattern>

          <Pattern
            label="Modal footer — equal-width pair"
            note="Both buttons flex-1, gap-2/3, side-by-side. NEVER a single full-width primary alone."
            classes={`flex items-center gap-3
both buttons: flex-1 h-12`}
          >
            <div className="flex items-center gap-3">
              <button className="flex-1 h-12 px-5 rounded-md bg-light text-heading text-14 font-bold border border-border hover:bg-light-hover transition-colors">
                Cancel
              </button>
              <button className="flex-1 h-12 px-5 rounded-md bg-primary text-[#FFFFFF] text-14 font-bold hover:bg-primary-hover shadow-sm">
                Save
              </button>
            </div>
          </Pattern>

          <Pattern
            label="Icon-only — round 33px (table actions)"
            classes={`w-9 h-9 rounded-full
border border-border bg-surface
text-gray-700 hover:bg-light`}
          >
            <div className="flex items-center gap-2">
              <button className="w-9 h-9 inline-flex items-center justify-center rounded-full border border-border bg-surface text-gray-700 hover:bg-light transition-colors">
                <IconShield size={16} stroke={2} />
              </button>
              <button className="w-9 h-9 inline-flex items-center justify-center rounded-full border border-border bg-surface text-gray-700 hover:bg-light transition-colors">
                <IconPencil size={16} stroke={2} />
              </button>
              <button className="w-9 h-9 inline-flex items-center justify-center rounded-full border border-border bg-surface text-gray-700 hover:text-danger hover:bg-danger-soft transition-colors">
                <IconTrash size={16} stroke={2} />
              </button>
            </div>
          </Pattern>
        </Section>

        {/* 4. BADGES */}
        <Section number="4" title="Badges / status pills" source="orders.html L1159+ · style.css:3175 + 2704+">
          <Pattern
            label="Soft variants (status indicators)"
            note="Saturated text + pale bg of the SAME hue. font-medium 13px. This is the canonical badge — use everywhere except for high-emphasis tier pills."
            classes={`inline-flex items-center
text-13 font-semibold
px-2.5 py-1 rounded
bg-{tone}-soft text-{tone}`}
          >
            <div className="flex flex-wrap gap-2">
              <span className="inline-flex items-center text-13 font-semibold px-2.5 py-1 rounded bg-success-soft text-success">Active</span>
              <span className="inline-flex items-center text-13 font-semibold px-2.5 py-1 rounded bg-danger-soft text-danger">Cancelled</span>
              <span className="inline-flex items-center text-13 font-semibold px-2.5 py-1 rounded bg-warning-soft text-warning">Pending</span>
              <span className="inline-flex items-center text-13 font-semibold px-2.5 py-1 rounded bg-info-soft text-info">Processing</span>
              <span className="inline-flex items-center text-13 font-semibold px-2.5 py-1 rounded bg-purple-soft text-purple">VIP</span>
            </div>
          </Pattern>

          <Pattern
            label="Solid variants (high emphasis — loyalty tiers, key callouts)"
            note="Solid coloured bg + white text + uppercase tracking-widest. Use sparingly — for things the eye should jump straight to."
            classes={`inline-flex items-center h-7 px-3 rounded
text-12 font-bold uppercase tracking-widest
bg-{tone} text-[#FFFFFF] shadow-sm`}
          >
            <div className="flex flex-wrap gap-2">
              <span className="inline-flex items-center text-12 font-bold uppercase tracking-widest px-3 h-7 rounded bg-orange text-[#FFFFFF] shadow-sm">Bronze</span>
              <span className="inline-flex items-center text-12 font-bold uppercase tracking-widest px-3 h-7 rounded bg-info text-[#FFFFFF] shadow-sm">Silver</span>
              <span className="inline-flex items-center text-12 font-bold uppercase tracking-widest px-3 h-7 rounded bg-warning text-[#FFFFFF] shadow-sm">Gold</span>
              <span className="inline-flex items-center text-12 font-bold uppercase tracking-widest px-3 h-7 rounded bg-purple text-[#FFFFFF] shadow-sm">Platinum</span>
            </div>
          </Pattern>
        </Section>

        {/* 5. FORM FIELDS */}
        <Section number="5" title="Form fields" source="categories.html, users.html, customer.html (Add modals)">
          <Pattern
            label="Single field group"
            note="Label is semibold 14px slate-900 — NEVER light grey. Required marker is a literal red asterisk inside the label. mb-5 between consecutive groups."
            classes={`label: block text-14 font-bold text-heading mb-2
input: w-full h-12 px-4 rounded-md
       border-2 border-border
       focus:border-primary
       text-14 font-medium text-heading`}
          >
            <div className="space-y-5">
              <div>
                <label className="block text-14 font-bold text-heading mb-2">
                  Customer name <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  defaultValue="Aaliyah Khan"
                  className="w-full h-12 px-4 rounded-md border-2 border-border focus:border-primary text-14 font-medium text-heading outline-none transition-colors"
                />
              </div>
              <div>
                <label className="block text-14 font-bold text-heading mb-2">
                  Phone <span className="text-13 text-gray-600 font-semibold">— for loyalty</span>
                </label>
                <input
                  type="tel"
                  placeholder="082 123 4567"
                  className="w-full h-12 px-4 rounded-md border-2 border-border focus:border-primary text-14 font-medium text-heading placeholder-gray-400 outline-none transition-colors tabular-nums"
                />
              </div>
            </div>
          </Pattern>
        </Section>

        {/* 6. CARDS */}
        <Section number="6" title="Cards" source="index.html (dashboard L943+) · style.css:5426+">
          <Pattern
            label="Default — p-5 (20px)"
            note="The standard card. p-5 default, p-3 only for compact KPI tiles."
            classes={`rounded-lg border border-border
bg-surface shadow-sm p-5`}
          >
            <div className="rounded-lg border border-border bg-surface shadow-sm p-5">
              <p className="text-12 font-bold uppercase tracking-wider text-gray-500 mb-1">Total Sales</p>
              <p className="text-22 font-bold text-heading tabular-nums">R 12,450</p>
              <p className="text-13 font-semibold text-success mt-1">+8.4% vs last week</p>
            </div>
          </Pattern>

          <Pattern
            label="KPI tile — compact p-3 (12px)"
            classes={`rounded-md border border-border
bg-surface p-3
flex items-center justify-between`}
          >
            <div className="rounded-md border border-border bg-surface p-3 flex items-center justify-between">
              <div>
                <p className="text-12 font-medium text-gray-600">Orders today</p>
                <p className="text-18 font-bold text-heading tabular-nums">42</p>
              </div>
              <div className="w-10 h-10 rounded-full bg-primary-soft text-primary inline-flex items-center justify-center">
                <IconCoffee size={18} stroke={2} />
              </div>
            </div>
          </Pattern>
        </Section>

        {/* 7. LIST ROWS */}
        <Section number="7" title="List rows (avatar + text + trailing meta)" source="users.html L1037-1056 (canonical)">
          <Pattern
            label="Customer / user row"
            note="Avatar 48px (touch-friendly POS) — gap-4 — name+sub stack — trailing meta. NEVER skip the gap. Sub-row is text-13 gray-700 minimum."
            classes={`flex items-center gap-4 px-5 py-5
rounded-lg border-2 border-border
hover:border-primary hover:bg-primary-soft`}
          >
            <button className="w-full flex items-center gap-4 px-5 py-5 rounded-lg bg-surface border-2 border-border hover:border-primary hover:bg-primary-soft transition-colors text-left">
              <div className="w-12 h-12 rounded-full inline-flex items-center justify-center font-bold text-15 flex-shrink-0 bg-warning-soft text-warning">
                AK
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2.5 leading-tight">
                  <p className="text-15 font-bold text-heading truncate">Aaliyah Khan</p>
                  <span className="inline-flex items-center text-12 font-bold uppercase tracking-widest px-3 h-7 rounded bg-warning text-[#FFFFFF] shadow-sm">Gold</span>
                </div>
                <div className="flex items-center gap-3 mt-1 leading-tight">
                  <p className="text-14 font-bold text-heading tabular-nums">082 123 4567</p>
                  <p className="text-13 font-semibold text-gray-700">2 days ago</p>
                </div>
              </div>
              <div className="text-right flex-shrink-0">
                <p className="inline-flex items-center gap-1 text-16 font-bold text-orange leading-tight">
                  <IconStar size={16} stroke={2} fill="currentColor" />
                  240
                </p>
                <p className="text-13 font-bold text-heading tabular-nums mt-1 leading-tight">R185 spent</p>
              </div>
            </button>
          </Pattern>
        </Section>

        {/* 8. MODALS */}
        <Section number="8" title="Modals" source="customers.html L1239+, addons.html (Add modals)">
          <Pattern
            label="Trigger an example modal"
            note="Modal max-h-[90vh] with overflow-y-auto on body so it never cuts off. Header: gap-4 icon + title + subtitle. Footer: equal-width Cancel + Primary."
          >
            <button
              onClick={() => setModalOpen(true)}
              className="h-12 px-5 inline-flex items-center gap-2 rounded-md bg-primary text-[#FFFFFF] text-14 font-bold hover:bg-primary-hover shadow-sm"
            >
              Open example modal
            </button>
          </Pattern>
        </Section>

        {/* 9. WELCOME / INFO */}
        <Section number="9" title="Welcome / info cards" source="dashboard pattern (h4 title rule)">
          <Pattern
            label="Welcome card with real heading"
            note="Title MUST be a real h-class heading (text-16 bold uppercase tracking-wide for banners). Body is text-13 medium leading-relaxed. p-5, gap-4 between icon and content."
            classes={`p-5 rounded-lg
bg-{tone}-soft border-2 border-{tone}/40
flex items-start gap-4`}
          >
            <div className="p-5 rounded-lg bg-success-soft border-2 border-success/40 flex items-start gap-4">
              <div className="w-11 h-11 rounded-md bg-success text-[#FFFFFF] inline-flex items-center justify-center flex-shrink-0 shadow-sm">
                <IconGift size={20} stroke={2.25} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-16 font-bold uppercase tracking-wide text-success leading-tight">
                  Welcome to the loyalty programme
                </p>
                <p className="text-13 font-medium text-heading mt-1.5 leading-snug">
                  Earn <span className="font-bold">1 point per Rand</span> spent.
                  Redeem at <span className="font-bold">100 pts = R10 reward</span>.
                  Tier upgrades unlock free drinks and birthday treats.
                </p>
              </div>
            </div>
          </Pattern>

          <Pattern
            label="Custom checkbox — guaranteed white tick on brand colour"
            note="Native <input type=checkbox> renders the tick in the user-agent default colour (often black on green) which looks broken. Use a styled <span> with sr-only checkbox underneath."
            classes={`<span class={isChecked ? 'bg-success text-[#FFFFFF]' : 'bg-surface border-2 border-gray-400'}>
  {isChecked && <IconCheck size={14} stroke={3.5} />}
</span>`}
          >
            <CheckboxDemo />
          </Pattern>
        </Section>

        {/* 10. COMMON MISTAKES */}
        <Section number="10" title="Common mistakes (Do / Don't)" source="lessons from CustomerPopover + ProductDetailsModal feedback">
          <DoDont
            doEl={
              <span className="inline-flex items-center text-12 font-bold uppercase tracking-widest px-3 h-7 rounded bg-warning text-[#FFFFFF] shadow-sm">Gold</span>
            }
            dontEl={
              <span className="inline-flex items-center text-10 font-medium uppercase tracking-wide px-1.5 h-4 rounded bg-warning-soft text-warning">Gold</span>
            }
            doNote="Solid bg + white text + tracking-widest h-7 px-3. Pops at a glance."
            dontNote="Pale text on pale bg, h-4 chip with text-10. Reads as 'almost invisible'."
          />

          <DoDont
            doEl={
              <div className="flex items-center gap-3">
                <button className="flex-1 h-12 px-5 rounded-md bg-light text-heading text-14 font-bold border border-border">Cancel</button>
                <button className="flex-1 h-12 px-5 rounded-md bg-primary text-[#FFFFFF] text-14 font-bold shadow-sm">Save</button>
              </div>
            }
            dontEl={
              <div className="flex items-center justify-between gap-2">
                <button className="text-13 text-gray-500">Cancel</button>
                <button className="text-13 text-primary font-medium">Save</button>
              </div>
            }
            doNote="Both buttons same height + shape, equal width side-by-side."
            dontNote="Bare text links. Reads as an afterthought, no clear primary action."
          />

          <DoDont
            doEl={
              <div className="flex items-center gap-4 p-4 bg-white border border-border rounded-md">
                <div className="w-12 h-12 rounded-full bg-warning-soft text-warning inline-flex items-center justify-center font-bold">AK</div>
                <div className="leading-tight">
                  <p className="text-15 font-bold text-heading">Aaliyah Khan</p>
                  <p className="text-13 font-semibold text-gray-700 mt-0.5">082 123 4567 · 2 days ago</p>
                </div>
              </div>
            }
            dontEl={
              <div className="flex items-center gap-1 p-2 bg-white border border-border rounded-md">
                <div className="w-12 h-12 rounded-full bg-warning-soft text-warning inline-flex items-center justify-center font-bold">AK</div>
                <div>
                  <p className="text-13 text-heading">Aaliyah Khan</p>
                  <p className="text-11 text-gray-400 mt-0.5">082 123 4567 · 2 days ago</p>
                </div>
              </div>
            }
            doNote="gap-4, p-4, name text-15 bold, sub text-13 semibold gray-700."
            dontNote="No gap (gap-1), no padding (p-2), name not bold, sub text-11 gray-400 (invisible)."
          />

          <DoDont
            doEl={
              <label className="block">
                <span className="block text-14 font-bold text-heading mb-2">Customer name <span className="text-danger">*</span></span>
                <input className="w-full h-12 px-4 border-2 border-border rounded-md text-14" defaultValue="Aaliyah" />
              </label>
            }
            dontEl={
              <label className="block">
                <span className="block text-12 text-gray-400 mb-1">Customer name</span>
                <input className="w-full h-8 px-2 border border-gray-200 rounded text-12" defaultValue="Aaliyah" />
              </label>
            }
            doNote="Label text-14 bold heading; input h-12 border-2."
            dontNote="Label text-12 gray-400 (forgettable); input h-8 thin border (looks template-y)."
          />
        </Section>

        <footer className="mt-16 pt-6 border-t border-gray-200 text-center">
          <p className="text-13 text-gray-600">
            CODED. POS · Design Guide · Synced with{' '}
            <code className="text-12 bg-gray-100 px-1.5 py-0.5 rounded">
              template-design-patterns.md
            </code>
          </p>
        </footer>
      </div>

      {/* Example modal */}
      <Modal open={modalOpen} onClose={() => setModalOpen(false)} hideClose size="md">
        <div className="flex items-start gap-4 mb-6 pb-5 border-b border-border">
          <div className="w-12 h-12 rounded-lg bg-primary-soft text-primary inline-flex items-center justify-center flex-shrink-0">
            <IconUser size={22} stroke={2} />
          </div>
          <div className="flex-1">
            <h3 className="text-18 font-bold text-heading leading-tight">Example modal</h3>
            <p className="text-14 font-medium text-gray-700 mt-1 leading-snug">
              Demonstrates the canonical modal pattern — header with icon tile, body with form, equal-width footer.
            </p>
          </div>
          <button
            onClick={() => setModalOpen(false)}
            className="w-9 h-9 inline-flex items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 transition-colors"
          >
            <IconX size={18} stroke={2} />
          </button>
        </div>

        <div className="space-y-5 mb-6">
          <div>
            <label className="block text-14 font-bold text-heading mb-2">
              Field label <span className="text-danger">*</span>
            </label>
            <input className="w-full h-12 px-4 rounded-md border-2 border-border focus:border-primary text-14 font-medium text-heading outline-none transition-colors" placeholder="Type something" />
          </div>
        </div>

        <div className="flex items-center gap-3 pt-5 border-t border-border">
          <button
            onClick={() => setModalOpen(false)}
            className="flex-1 h-12 px-5 rounded-md bg-light text-heading text-14 font-bold border border-border hover:bg-light-hover transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => setModalOpen(false)}
            className="flex-1 h-12 px-5 rounded-md bg-primary text-[#FFFFFF] text-14 font-bold hover:bg-primary-hover shadow-sm"
          >
            Save
          </button>
        </div>
      </Modal>
    </div>
  );
}

function CheckboxDemo() {
  const [on, setOn] = useState(true);
  return (
    <label className="inline-flex items-center gap-3 cursor-pointer select-none group">
      <span className={[
        'w-5 h-5 rounded inline-flex items-center justify-center transition-colors',
        on ? 'bg-success text-[#FFFFFF]' : 'bg-surface border-2 border-gray-400 group-hover:border-success',
      ].join(' ')}>
        {on && <IconCheck size={14} stroke={3.5} />}
      </span>
      <input type="checkbox" checked={on} onChange={(e) => setOn(e.target.checked)} className="sr-only" />
      <span className="text-14 font-bold text-heading">Send welcome SMS</span>
    </label>
  );
}
